/**
 * SOLID Review
 * S — Strong. One concern: the ad config decisions, each built from the snapshot as an `AdSlot`, asked, and answered
 *     through a `ClientDecision`. Whom to ask for and what to keep are the slot's rules.
 * O — Good. A new provider needs no change here.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. One role, `IServiceDecisionTable`: the Facade sees the ads ops' table and nothing else.
 * D — Strong. The ad slot, the decision builder and the schemas.
 */
import { AdSlot } from '../../ads/AdSlot';
import { ClientDecision, type TClientDecision } from '../ClientDecision';
import type { IServiceDecisionTable, TDecisionRows } from '../IServiceDecisionTable';
import type { IPropsClientInput } from '../../ports/schemas/ClientChanges';
import type { IEntityTableAck } from '../../ports/schemas/TableEvents';
import type { IEntityAdConfigPlan, ICommandAdConfigLoaded } from '../../ports/schemas/AdsMoves';

/** The ops this service answers for on the client Domain's lollipop. */
type TAdsOp = 'planAdConfig' | 'adConfigLoaded';

/**
 * THE GAME SCREEN'S AD CONFIG (frontend-domain-oo P6, 2026-10-26; handbook §28.273) — the decide functions of
 * `decisions/ads/adSlot.ts` (FE4d, useAdProviderConfig's) as a service. Which zone the screen shows is the slot's
 * query, read when the view is built.
 */
export class Ads implements IServiceDecisionTable<TAdsOp> {
  /** Its decisions, by op: what `ClientDecisions` merges and answers through. */
  readonly decisions: TDecisionRows<TAdsOp> = {
    planAdConfig: (input) => this.planAdConfig(input),
    adConfigLoaded: (input, command) => this.adConfigLoaded(input, command),
  };

  /** Whether to ask for the seated player's ad config. */
  planAdConfig(input: IPropsClientInput): TClientDecision<IEntityAdConfigPlan> {
    const slot = AdSlot.of(input.session);
    const plan: IEntityAdConfigPlan = { request: slot.configAsked() };
    return ClientDecision.keeping(input, slot).answering(plan);
  }

  /** The server's answer for a player. */
  adConfigLoaded(input: IPropsClientInput, command: ICommandAdConfigLoaded): TClientDecision<IEntityTableAck> {
    const slot = AdSlot.of(input.session);
    return ClientDecision.keeping(input, slot).acked(slot.configLoaded(command));
  }
}
